/**
 * `settings` namespace — Korean translations.
 *
 * Mirrors `en/settings.ts` exactly; the `Record<SettingsKey, string>` annotation
 * makes a missing or misspelled key a compile error, so this file cannot drift
 * from the canonical English source. The arm-switch copy is SAFETY-RELEVANT — the
 * "실제 자금", "글로벌 킬 스위치", "verified Toss 자격 증명 필요" 의미를 정확히
 * 보존합니다. Concise 명사형/합니다체. Tech nouns (API/OAuth/JSON/Expo/FCM/APNs)
 * and brand/ticker names stay English.
 */
import type { SettingsKey } from "../en/settings";

export const settings: Record<SettingsKey, string> = {
  // ── hub ──
  hubEyebrow: "구성",
  hubDescription:
    "브로커와 AI를 연결하고 푸시를 등록한 뒤 실거래를 활성화합니다. 실거래 비활성 상태로 배포됩니다.",
  dryRunDefaultOn: "새 전략은 기본적으로 모의 실행됩니다 — 주문이 시뮬레이션됩니다.",
  dryRunDefaultOff: "새 전략은 기본적으로 실제 주문으로 실행됩니다.",
  manageArmingDefaults: "실거래 활성화 및 기본값 관리",
  unlocked: "잠금 해제됨",
  locked: "잠김",

  // ── feature-gate cards (catalog.ts) ──
  gateAiTitle: "AI 제공자",
  gateAiUnlocks: "전략 빌더 채팅과 AI 어드바이저 게이트를 잠금 해제합니다.",
  gateAiUnlockedNote: "전략 빌더와 어드바이저가 활성화되었습니다.",
  gateAiLockedCta: "AI 키 추가",
  gateTossTitle: "Toss Securities",
  gateTossUnlocks: "실시간 시장 데이터, 계좌 동기화, 주문, 실거래 활성화를 잠금 해제합니다.",
  gateTossUnlockedNote: "브로커가 연결되었습니다 — 실거래를 활성화할 수 있습니다.",
  gateTossLockedCta: "Toss 연결",
  gatePushTitle: "모바일 푸시",
  gatePushUnlocks: "승인 필요 전략과 모바일 승인/거부를 잠금 해제합니다.",
  gatePushUnlockedNote: "푸시가 구성되었습니다 — 승인 필요 모드를 사용할 수 있습니다.",
  gatePushLockedCta: "푸시 설정",

  // ── credential verify-status labels (catalog.ts) ──
  verifyVerified: "인증됨",
  verifyFailed: "실패",
  verifyUntested: "미테스트",
  verifyNotSet: "미설정",

  // ── shared credential controls (CredentialControls.tsx) ──
  verifiedAt: "{date} 인증됨",
  testConnection: "연결 테스트",
  removeConfirm: "{kind} 자격 증명을 제거하시겠습니까? 해당 기능이 다시 잠깁니다.",
  testFailed: "테스트를 실행하지 못했습니다. 다시 시도해 주세요.",
  connectionVerified: "연결이 인증되었습니다 — 기능이 잠금 해제되었습니다.",
  verificationFailed: "인증에 실패했습니다.",
  removeFailed: "자격 증명을 제거하지 못했습니다.",

  // ── general · page ──
  generalEyebrow: "설정 · 일반",
  generalTitle: "일반",
  generalDescription: "시간대, 기준 통화, 글로벌 실거래 스위치, 알림 전달 설정.",

  // ── general · language (bilingual by design so it is findable in either mode) ──
  languageTitle: "언어 / Language",
  languageDescription: "인터페이스 언어. 즉시 적용되며 이 기기에 저장됩니다.",
  languageHint: "한국어 / English",

  // ── general · operational defaults ──
  defaultsTitle: "운영 기본값",
  defaultsDescription: "전략이 스케줄을 맞추는 기준 시계, 리포트 기준 통화, 새 전략의 안전 기본값.",
  timezoneLabel: "시간대",
  timezoneHint: "IANA 시간대 이름.",
  baseCurrencyLabel: "기준 통화",
  baseCurrencyHint: "포트폴리오 분해의 리포트 기준.",
  currencyKrw: "KRW — 대한민국 원",
  currencyUsd: "USD — 미국 달러",
  defaultDryRunLabel: "새 전략을 기본 모의 실행으로 설정",
  defaultDryRunDescription: "권장. 새 전략은 실제 주문을 넣는 대신 체결을 시뮬레이션합니다 (§0.5).",
  saveDefaults: "기본값 저장",

  // ── general · notifications section ──
  notificationsDescription:
    "각 이벤트 유형을 웹, 푸시, 이메일로 라우팅하고 우선순위 하한과 방해 금지 시간을 설정합니다.",

  // ── general · live trading (arm master switch — SAFETY) ──
  liveTradingTitle: "실거래",
  liveTradingDescription:
    "글로벌 킬 스위치. 비활성 상태에서는 전략 자체 모드와 관계없이 모든 주문이 시뮬레이션되며 실제 주문은 발생하지 않습니다.",
  armedBadge: "실거래 활성",
  disarmedBadge: "실거래 비활성",
  armLiveTrading: "실거래 활성화",
  armDescriptionArmed: "실거래 활성 상태이며 모의 실행이 아닌 전략은 실제 주문을 넣을 수 있습니다.",
  armDescriptionDisarmed: "실제 주문을 허용하려면 켜세요. 인증된 Toss 자격 증명이 필요합니다.",
  tossRequiredBeforeArm: "활성화하기 전에 인증된 Toss 자격 증명이 필요합니다.",
  connectTossLink: "Toss 연결 →",
  armErrorNotReady:
    "활성화하기 전에 인증된 Toss 자격 증명이 필요합니다 (Toss 설정에서 연결하세요).",
  armErrorGeneric: "실거래 상태를 변경하지 못했습니다. 다시 시도해 주세요.",
  confirmHeading: "실제 주문이 활성화됩니다",
  confirmBody:
    "활성화하면 모의 실행이 아닌 모든 활성 전략이 실제 자금으로 Toss 계좌에 실제 주문을 넣을 수 있습니다. 각 전략은 여전히 자체 모의 실행 플래그와 리스크 한도를 따르며, 이 스위치는 글로벌 차단만 해제합니다.",
  confirmAck: "실제 주문이 실제 자금을 사용한다는 것을 이해합니다.",

  // ── AI page ──
  aiEyebrow: "설정 · AI",
  aiDescription:
    "OpenAI 또는 Anthropic 키를 추가하면 전략 빌더와 어드바이저가 잠금 해제됩니다. 인증된 제공자가 하나 이상이면 기능이 잠금 해제됩니다.",
  openaiKeyHint: "표준 OpenAI API 키 (Bearer). 암호화되어 저장되며 다시 반환되지 않습니다.",
  anthropicKeyHint:
    "기본적으로 `x-api-key` 자격 증명입니다. 보유한 경우에만 dev-platform bearer를 사용하세요.",
  defaultModelPrefix: "기본 모델:",
  credentialTypeLabel: "자격 증명 유형",
  credentialTypeApiKey: "API key (x-api-key)",
  credentialTypeBearer: "Dev-platform bearer",
  defaultModelLabel: "기본 모델",
  bearerTokenLabel: "Bearer token",
  apiKeyLabel: "API key",
  replaceStoredHint: "저장된 자격 증명을 교체하려면 새 값을 입력하세요.",
  storedEncryptedHint: "암호화되어 저장됩니다 (AES-256-GCM). 브라우저로 다시 반환되지 않습니다.",
  betaHeaderLabel: "Beta header (선택)",
  betaHeaderHint: "dev-platform bearer의 경우 `anthropic-beta`로 전송됩니다.",
  replaceKey: "키 교체",
  saveKey: "키 저장",
  saveCredentialFailed: "자격 증명을 저장하지 못했습니다.",

  // ── AI page · custom (OpenAI-compatible) provider ──
  customTitle: "커스텀 (OpenAI 호환)",
  customKeyHint:
    "OpenAI 호환 API면 모두 사용 가능합니다 — OpenRouter, Ollama Cloud, 로컬 Ollama, LM Studio, vLLM. 키가 없는 로컬 서버는 아무 값이나 키로 입력하세요.",
  baseUrlLabel: "Base URL",
  baseUrlHint: "OpenAI 호환 엔드포인트. 예: https://openrouter.ai/api/v1.",
  customApiKeyHint: "엔드포인트용 Bearer 키. 키가 없는 로컬 서버는 아무 값이나 입력하세요.",
  baseUrlRequired: "Base URL이 필요합니다.",

  // ── AI page · model aliases ──
  aliasesTitle: "모델 별칭",
  aliasesDescription:
    "각 내부 역할에 어떤 제공자와 모델을 사용할지 선택합니다. 위에서 등록한 제공자만 선택할 수 있으며, 모델 입력란은 해당 제공자의 모델 목록으로 자동 완성됩니다 — 원하는 모델 id를 직접 입력할 수도 있습니다.",
  aliasChatBuilder: "채팅 빌더",
  aliasAdvisor: "AI 어드바이저",
  aliasClassify: "분류",
  aliasProviderLabel: "제공자",
  aliasModelLabel: "모델",
  aliasProviderCustom: "커스텀 (OpenAI 호환)",
  aliasProviderUnregistered: " (미등록)",
  aliasModelsLoading: "사용 가능한 모델을 불러오는 중…",
  aliasModelsError: "모델 목록을 불러오지 못했습니다 — 직접 입력하세요.",
  aliasUsingDefault: "내장 기본값:",
  aliasesHint:
    "위에서 제공자를 등록하면 모델 목록이 잠금 해제됩니다. 직접 입력한 값도 대체 입력으로 그대로 동작합니다.",
  saveAliases: "별칭 저장",
  saveAliasesFailed: "모델 별칭을 저장하지 못했습니다.",

  // ── agent tools page ──
  toolsEyebrow: "설정 · 에이전트 도구",
  toolsTitle: "에이전트 도구",
  toolsDescription:
    "각 AI 에이전트가 사용할 수 있는 도구를 선택합니다. 비활성화된 도구는 모델에 노출되지 않으며, 변경은 다음 채팅 턴 또는 어드바이저 판단부터 적용됩니다.",
  toolsHubDescription: "채팅 빌더와 어드바이저의 개별 도구를 켜거나 끕니다.",
  manageAgentTools: "에이전트 도구 관리",
  toolsChatTitle: "채팅 에이전트 (전략 빌더)",
  toolsChatDescription:
    "전략 빌더 채팅이 쓸 수 있는 도구입니다. 지연 로드 도구는 모델이 tool_search로 발견하기 전까지 프롬프트에 포함되지 않습니다.",
  toolsAdvisorTitle: "어드바이저 에이전트",
  toolsAdvisorDescription:
    "AI 어드바이저가 주문을 승인·축소·거부하기 전에 호출할 수 있는 조사 도구입니다. 전부 끄면 어드바이저는 단발 판단으로 동작합니다.",
  toolCoreBadge: "핵심",
  toolLazyBadge: "지연 로드",
  toolCoreHint: "에이전트 동작에 필수 — 비활성화할 수 없습니다.",
  saveTools: "변경 사항 저장",
  saveToolsFailed: "도구 설정을 저장하지 못했습니다.",
  toolsUnsavedHint: "저장되지 않은 변경 사항이 있습니다.",

  // ── agent tools · per-tool descriptions (slot names stay English) ──
  toolDescEditStrategy: "전략 초안을 편집합니다 — 빌더의 핵심 메커니즘.",
  toolDescWebSearch: "최신 컨텍스트(뉴스, 실적, 매크로)를 웹에서 검색합니다.",
  toolDescFetchPage: "웹 검색으로 발견한 페이지를 가져와 상세를 확인합니다.",
  toolDescSaveStrategy: "초안을 전략으로(또는 기존 전략에) 저장합니다.",
  toolDescRunBacktest: "연결된 전략의 비동기 백테스트를 큐에 넣습니다.",
  toolDescLoadStrategyStats: "전략의 실거래 승률/손익비 통계를 불러옵니다.",
  toolDescLoadBacktestResults: "가장 최근 완료된 백테스트 지표를 불러옵니다.",
  toolDescLoadAdvisorDecisions: "프롬프트 튜닝용 과거 어드바이저 판단을 불러옵니다.",
  toolDescResearchWeb: "주제에 대해 제한된 웹 리서치 서브에이전트를 실행합니다.",
  toolDescSnapshotSchedule: "자동 포트폴리오 스냅샷 일정을 조회/수정합니다.",
  toolDescTakeSnapshot: "지금 즉시 포트폴리오 스냅샷을 기록합니다.",
  toolDescGetTrend: "종목의 최근 일봉 추세 요약.",
  toolDescGetHoldings: "손익을 포함한 현재 계좌 보유 종목 전체.",
  toolDescGetTradeHistory: "전략의 최근 체결 내역.",
  toolDescGetDecisionHistory: "전략의 최근 어드바이저 판단 이력.",
  toolDescGetPortfolioSummary: "실현 손익 실적과 계좌 전체 수익률.",

  // ── Toss page ──
  tossEyebrow: "설정 · 브로커",
  tossDescription:
    "Toss OpenAPI 자격 증명을 연결하면 실시간 시장 데이터, 거래, 계좌 동기화가 잠금 해제됩니다.",
  apiCredentialsTitle: "API 자격 증명",
  apiCredentialsDescription:
    "토스증권 WTS의 설정 → Open API 메뉴에서 발급합니다. 두 값 모두 암호화되어 저장되며, 마스킹된 끝자리만 표시됩니다.",
  clientIdLabel: "API Key (tsck_…)",
  clientSecretLabel: "Secret Key (tssk_…)",
  replaceCredentials: "자격 증명 교체",
  saveCredentials: "자격 증명 저장",
  saveCredentialsFailed: "자격 증명을 저장하지 못했습니다.",
  accountsTitle: "계좌",
  accountsLockedDescription: "브로커 자격 증명이 인증되면 Toss 계좌를 동기화할 수 있습니다.",
  verifyTossToSync: "Toss를 인증하여 계좌 동기화",
  verifyTossToSyncDescription:
    "위 자격 증명을 테스트하세요. 연결이 인증되면 계좌 동기화와, 일반 설정에서 실거래 활성화가 잠금 해제됩니다.",
  accountsDescription:
    "Toss에서 캐시됨 (getAccounts는 1 req/s로 제한). 새로고침하려면 동기화하세요.",
  syncNow: "지금 동기화",
  syncFailed: "동기화에 실패했습니다. 다시 시도해 주세요.",
  noAccounts: "아직 계좌가 없습니다",
  noAccountsDescription: "동기화를 실행하여 Toss 계좌를 가져오세요.",
  syncAccounts: "계좌 동기화",
  primaryBadge: "기본",

  // ── push page ──
  pushEyebrow: "설정 · 푸시",
  pushDescription: "푸시를 구성하여 승인 필요 전략과 모바일 승인/거부를 활성화합니다.",
  pushUnlocked: "푸시 잠금 해제됨",
  pushLocked: "푸시 잠김",
  expoTokenTitle: "Expo access token",
  expoTokenDescription:
    "Expo의 향상된 푸시 보안을 위한 선택 토큰. 인증하면 푸시 기능 게이트가 잠금 해제됩니다.",
  accessTokenLabel: "Access token",
  accessTokenPlaceholder: "Expo access token",
  replaceTokenHint: "저장된 토큰을 교체하려면 새 값을 입력하세요.",
  tokenStoredHint:
    "암호화되어 저장됩니다. 전송에는 서버 키가 필요 없지만, 인증된 토큰이 게이트를 잠금 해제합니다.",
  replaceToken: "토큰 교체",
  saveToken: "토큰 저장",
  saveTokenFailed: "토큰을 저장하지 못했습니다.",
  prerequisitesTitle: "사전 요구 사항",
  prerequisitesDescription:
    "푸시가 완전히 구성될 때까지 승인 필요 모드는 잠긴 상태로 유지됩니다. 이 단계는 건너뛸 수 없습니다.",
  prereqAndroidTitle: "Android — FCM V1 서비스 계정 JSON",
  prereqAndroidDetail: "Firebase Cloud Messaging V1 서비스 계정을 EAS 자격 증명에 업로드하세요.",
  prereqIosTitle: "iOS — APNs 키 + 유료 Apple Developer 계정",
  prereqIosDetail: "Apple Push 키와 유료 멤버십. 인증서는 EAS가 관리합니다.",
  prereqDeviceTitle: "모바일 앱에서 기기 등록",
  prereqDeviceDetail: "richrich 앱을 열고 알림을 허용하면 Expo push token이 등록됩니다.",

  // ── mobile QR sign-in (general page) ──
  qrSignInTitle: "모바일 앱 로그인",
  qrSignInDescription:
    "코드를 입력하지 않고 richrich 앱에 로그인하세요. QR을 생성해 휴대폰 카메라로 스캔하면 됩니다.",
  qrSignInGenerate: "QR 코드 생성",
  qrSignInRegenerate: "새 코드 생성",
  qrSignInScanHint: "휴대폰 카메라로 스캔하면 richrich 앱이 열리며 바로 로그인됩니다.",
  qrSignInExpiresIn: "1회용 · {seconds}초 후 만료",
  qrSignInExpired: "코드가 만료되었습니다.",
  qrSignInFailed: "QR 코드를 생성하지 못했습니다. 다시 시도하세요.",
};
